import { useEffect, useState } from "react";
import { desktop, type DesktopStatusData, type PrintCategory, type PrintSettings } from "./desktop";

const CATS: { key: PrintCategory; title: string; hint: string }[] = [
  { key: "label", title: "Этикетки (60×40 мм)", hint: "Термопринтер этикеток" },
  { key: "a4", title: "Документы A4", hint: "Акты, АВР, договоры, счета" },
  { key: "receipt", title: "Чековая лента", hint: "Чековый принтер" },
];

const panel: React.CSSProperties = {
  position: "fixed", left: typeof window !== "undefined" && window.innerWidth > 768 ? 232 : 12, bottom: 44, width: 380, maxHeight: "70vh", overflowY: "auto", zIndex: 100000,
  background: "#11151f", border: "1px solid #2a3344", borderRadius: 12, padding: 14, color: "#e5e7eb",
  boxShadow: "0 12px 40px rgba(0,0,0,.5)", fontSize: 13,
};
const btn: React.CSSProperties = { padding: "6px 12px", borderRadius: 8, border: "1px solid #374151", background: "#1a2030", color: "#e5e7eb", cursor: "pointer", fontSize: 12 };
const btnPri: React.CSSProperties = { ...btn, background: "#10b981", borderColor: "#10b981", color: "#fff", fontWeight: 700 };

const ago = (t: number | null) => {
  if (!t) return "ещё не было";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return "только что";
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`;
  return new Date(t).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function PrintSettingsView({ onBack }: { onBack: () => void }) {
  const [settings, setSettings] = useState<PrintSettings | null>(null);
  const [printers, setPrinters] = useState<{ name: string; isDefault: boolean }[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    desktop!.getPrintSettings().then(setSettings);
    desktop!.listPrinters().then(setPrinters);
  }, []);

  if (!settings) return <div>Загрузка…</div>;
  const upd = (k: PrintCategory, patch: Partial<PrintSettings[PrintCategory]>) => { setSaved(false); setSettings({ ...settings, [k]: { ...settings[k], ...patch } }); };
  const sel: React.CSSProperties = { width: "100%", padding: "7px 8px", borderRadius: 8, background: "#1a2030", color: "#f9fafb", border: "1px solid #374151", fontSize: 13 };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <button style={btn} onClick={onBack}>← Назад</button>
        <b style={{ fontSize: 14 }}>Настройки печати</b>
      </div>
      {CATS.map(({ key, title, hint }) => (
        <div key={key} style={{ border: "1px solid #1f2937", borderRadius: 10, padding: 10, marginBottom: 10 }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 6 }}>{hint}</div>
          <select style={sel} value={settings[key].printer} onChange={(e) => upd(key, { printer: e.target.value })}>
            <option value="">— принтер не выбран —</option>
            {printers.map((p) => <option key={p.name} value={p.name}>{p.name}{p.isDefault ? " (по умолчанию)" : ""}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={settings[key].auto} onChange={(e) => upd(key, { auto: e.target.checked })} />
              Печатать автоматически
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
              Копий
              <input type="number" min={1} max={10} value={settings[key].copies}
                onChange={(e) => upd(key, { copies: Number(e.target.value) || 1 })}
                style={{ width: 52, padding: "4px 6px", borderRadius: 6, background: "#1a2030", color: "#f9fafb", border: "1px solid #374151" }} />
            </label>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button style={btnPri} onClick={async () => { await desktop!.setPrintSettings(settings); setSaved(true); }}>Сохранить</button>
        {saved && <span style={{ color: "#10b981" }}>Сохранено</span>}
      </div>
      <div style={{ color: "#6b7280", fontSize: 11, marginTop: 10 }}>
        Размер бумаги для этикеток задайте в драйвере принтера (60×40 мм). Этикетка и чек печатаются без масштабирования.
      </div>
    </>
  );
}

/** Индикатор связи, очередь синхронизации, обновления и настройки печати — только в десктопном приложении */
export default function DesktopStatus() {
  const [s, setS] = useState<DesktopStatusData | null>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"main" | "print">("main");
  const [toasts, setToasts] = useState<{ id: number; message: string; kind: string }[]>([]);

  useEffect(() => {
    if (!desktop) return;
    desktop.getStatus().then(setS);
    const off = desktop.onStatus(setS);
    return off;
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const { message, kind } = (e as CustomEvent).detail;
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, kind }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 8000 : 3500);
    };
    window.addEventListener("desktop-toast", h);
    return () => window.removeEventListener("desktop-toast", h);
  }, []);

  if (!desktop || !s) return null;

  const failed = s.failed.length;
  const pending = s.pending.length;
  const color = failed ? "#ef4444" : !s.online ? "#f59e0b" : pending || s.syncing ? "#3b82f6" : "#10b981";
  const label = failed
    ? `Ошибки синхронизации: ${failed}`
    : !s.online ? `Нет связи${pending ? ` · в очереди ${pending}` : ""}`
      : s.syncing ? "Синхронизация…"
        : pending ? `В очереди: ${pending}` : "Онлайн";

  return (
    <>
      {s.mustUpdate && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200000, background: "rgba(5,8,15,.96)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
          <div style={{ maxWidth: 420 }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Требуется обновление</div>
            <div style={{ color: "#9ca3af", marginBottom: 14 }}>
              Версия {s.version} больше не поддерживается. {s.update.state === "downloading" ? `Загрузка обновления… ${s.update.progress}%` : s.update.state === "ready" ? "Обновление готово." : "Подключитесь к интернету — обновление скачается автоматически."}
            </div>
            {s.update.state === "ready" && <button style={btnPri} onClick={() => desktop!.installUpdate()}>Установить и перезапустить</button>}
          </div>
        </div>
      )}

      {s.stale && (
        <div style={{ position: "fixed", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 100002, maxWidth: "92vw", padding: "10px 16px", borderRadius: 10, background: "#4a2a05", border: "1px solid #f59e0b", color: "#fde68a", fontSize: 13, textAlign: "center", boxShadow: "0 8px 30px rgba(0,0,0,.5)" }}>
          📶 Нет связи с сервером уже {Math.max(60, Math.round((Date.now() - (s.lastContact ?? Date.now())) / 60000)) >= 90 ? `${Math.floor((Date.now() - (s.lastContact ?? Date.now())) / 3600000)} ч` : "больше часа"}.
          {pending > 0 ? ` Не отправлено: ${pending}.` : " Данные не обновлялись."} Подключите интернет (например, раздайте с телефона) — всё выгрузится автоматически.
        </div>
      )}

      <div style={{ position: "fixed", left: typeof window !== "undefined" && window.innerWidth > 768 ? 232 : 12, bottom: 10, zIndex: 100000, display: "flex", gap: 6 }}>
        <button onClick={() => { setOpen((o) => !o); setView("main"); }} title="Связь, синхронизация, печать"
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 999, border: `1px solid ${color}`, background: "#0e131d", color: "#e5e7eb", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}` }} />
          {label}
          {s.update.state === "ready" && <span style={{ color: "#10b981" }}> · обновление готово</span>}
        </button>
      </div>

      {open && (
        <div style={panel}>
          {view === "print" ? <PrintSettingsView onBack={() => setView("main")} /> : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <b style={{ fontSize: 14 }}>Keyoff Group CRM · {s.version}</b>
                <button style={btn} onClick={() => setOpen(false)}>✕</button>
              </div>
              <div style={{ color: "#9ca3af", marginBottom: 10 }}>
                {s.online ? "Связь с сервером есть." : "Работа без интернета: данные из локальной копии, изменения сохраняются и будут отправлены позже."}
                <br />Последняя синхронизация: {ago(s.lastSync)}{s.prefetching ? " · обновляю данные…" : ""}
              </div>

              {s.authRequired && (
                <div style={{ background: "#3b1d1d", border: "1px solid #7f1d1d", borderRadius: 8, padding: 8, marginBottom: 10 }}>
                  Сессия истекла — войдите заново, и очередь изменений отправится автоматически.
                </div>
              )}

              {pending > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Ждут отправки ({pending})</div>
                  {s.pending.map((i) => (
                    <div key={i.id} style={{ padding: "4px 0", borderTop: "1px solid #1f2937", fontSize: 12 }}>{i.text}</div>
                  ))}
                </div>
              )}

              {failed > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: "#f87171" }}>Не удалось отправить ({failed})</div>
                  {s.failed.map((i) => (
                    <div key={i.id} style={{ padding: "6px 0", borderTop: "1px solid #1f2937", fontSize: 12 }}>
                      <div>{i.text}</div>
                      <div style={{ color: "#f87171" }}>{i.error}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        <button style={btn} onClick={() => desktop!.retryFailed(i.id)}>Повторить</button>
                        <button style={btn} onClick={() => { if (window.confirm("Отбросить это изменение? Оно не попадёт на сервер.")) desktop!.discardFailed(i.id); }}>Отбросить</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <button style={btnPri} disabled={s.syncing} onClick={() => desktop!.syncNow()}>Синхронизировать</button>
                <button style={btn} onClick={() => setView("print")}>🖨 Настройки печати</button>
              </div>

              <div style={{ borderTop: "1px solid #1f2937", paddingTop: 8, color: "#9ca3af" }}>
                {s.update.state === "ready" && (
                  <div style={{ marginBottom: 6 }}>
                    Версия {s.update.version} скачана.{" "}
                    <button style={btnPri} onClick={async () => { const r = await desktop!.installUpdate(); if (!r.ok && r.error) alert(r.error); }}>Установить и перезапустить</button>
                  </div>
                )}
                {s.update.state === "downloading" && <div>Загрузка обновления {s.update.version}… {s.update.progress}%</div>}
                {s.update.state === "error" && <div style={{ color: "#f87171" }}>Ошибка обновления: {s.update.error}</div>}
                {(s.update.state === "idle" || s.update.state === "checking") && (
                  <div>Установлена последняя версия{s.latestVersion && s.latestVersion !== s.version ? ` (на сервере ${s.latestVersion})` : ""}.{" "}
                    <button style={btn} onClick={() => desktop!.checkUpdates()}>Проверить обновления</button></div>
                )}
                {s.updateNotes && s.latestVersion === s.version && <div style={{ fontSize: 11, marginTop: 4 }}>{s.updateNotes}</div>}
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 100001, display: "flex", flexDirection: "column", gap: 8 }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ maxWidth: 360, padding: "10px 14px", borderRadius: 10, background: t.kind === "error" ? "#3b1d1d" : "#10281f", border: `1px solid ${t.kind === "error" ? "#7f1d1d" : "#14532d"}`, color: "#f9fafb", fontSize: 13 }}>
            {t.message}
          </div>
        ))}
      </div>
    </>
  );
}
